import { Observable } from 'rxjs';

import { SnLonLat } from '@lib/geo/helpers';
import { MapMarkerIcon } from '@lib/geo/map/core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { FsMapPopup } from '@lib/geo/map/core/popup';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces/markers/marker-events.interface';
import { IMarkerOptions } from '@lib/geo/map/interfaces/markers/marker-options.interface';
import { IMarker } from '@lib/geo/map/interfaces/markers/marker.interface';
import { SnBadgeConfig } from '@lib/ui/badge';

import { IFsMap } from '../types';

export abstract class FsMapMarkersServiceProvider<TMap extends IFsMap, TMarker extends IMarker<TMap> = IMarker<TMap>> {
  abstract readonly geospatialProvider: string;
  abstract readonly id: string;

  abstract setMap(map: TMap): void;

  // Core marker operations
  abstract addMarker(marker: IMarkerOptions): TMarker;
  abstract removeMarker(marker: TMarker): void;
  abstract updateMarker(marker: TMarker, options: Partial<IMarkerOptions>): void;

  // Marker lifecycle and state management
  abstract setVisible(marker: TMarker, visible: boolean): void;
  abstract setPosition(marker: TMarker, position: SnLonLat): void;
  abstract setIcon(marker: TMarker, icon: MapMarkerIcon): void;
  abstract setStyle(marker: TMarker, style: IMapMarkerStyleOptions): void;
  abstract setDraggable(marker: TMarker, draggable: boolean): void;
  abstract setRotation(marker: TMarker, rotation: number): void;
  abstract setTitle(marker: TMarker, title: string): void;

  // Marker properties and data access
  abstract getMarkerId(marker: TMarker): string;
  abstract getMarkerPosition(marker: TMarker): SnLonLat;
  abstract getMarkerTitle(marker: TMarker): string;
  abstract getMarkerAttributes(marker: TMarker): Record<string, unknown>;
  abstract getMarkerCenter(marker: TMarker): { center: number[] };

  // Badge management
  abstract setBadge(marker: TMarker, badge: SnBadgeConfig): void;
  abstract getBadge(marker: TMarker): SnBadgeConfig;
  abstract createBadge(marker: TMarker, badgeClass?: string): void;
  abstract createSelectedBadge(marker: TMarker): void;
  abstract removeSelectedBadge(marker: TMarker): void;
  abstract setBadgeStatus(
    marker: TMarker,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void;

  // Popup management
  abstract setPopup(marker: TMarker, popup: FsMapPopup<TMap>): void;

  // Event handling - Mouse and Touch Events
  abstract onMarkerClick(marker: TMarker): Observable<IMarkerClickEvent<TMap>>;
  abstract onMarkerDoubleClick(marker: TMarker): Observable<IMarkerDoubleClickEvent<TMap>>;
  abstract onMarkerRightClick(marker: TMarker): Observable<IMarkerRightClickEvent<TMap>>;
  abstract onMarkerMouseOver(marker: TMarker): Observable<IMarkerMouseOverEvent<TMap>>;
  abstract onMarkerMouseOut(marker: TMarker): Observable<IMarkerMouseOutEvent<TMap>>;
  abstract onMarkerMouseEnter(marker: TMarker): Observable<IMarkerMouseEnterEvent<TMap>>;
  abstract onMarkerMouseLeave(marker: TMarker): Observable<IMarkerMouseLeaveEvent<TMap>>;

  // Event handling - Drag Events
  abstract onMarkerDragStart(marker: TMarker): Observable<IMarkerDragStartEvent<TMap>>;
  abstract onMarkerDrag(marker: TMarker): Observable<IMarkerDragMoveEvent<TMap>>;
  abstract onMarkerDragEnd(marker: TMarker): Observable<IMarkerDragEndEvent<TMap>>;

  // Event handling - State Change Events
  abstract onMarkerPositionChange(marker: TMarker): Observable<IMarkerPositionChangeEvent<TMap>>;
  abstract onMarkerTitleChange(marker: TMarker): Observable<IMarkerTitleChangeEvent<TMap>>;
  abstract onMarkerVisibilityChange(
    marker: TMarker,
  ): Observable<IMarkerVisibilityChangeEvent<TMap>>;
  abstract onMarkerSelectionChange(
    marker: TMarker,
  ): Observable<IMarkerSelectionChangeEvent<TMap>>;
  abstract onMarkerIconChange(marker: TMarker): Observable<IMarkerIconChangeEvent<TMap>>;
  abstract onMarkerStyleChange(marker: TMarker): Observable<IMarkerStyleChangeEvent<TMap>>;

  // Event handling - Lifecycle Events
  abstract onMarkerAdd(marker: TMarker): Observable<IMarkerAddEvent<TMap>>;
  abstract onMarkerRemove(marker: TMarker): Observable<IMarkerRemoveEvent<TMap>>;
  abstract onMarkerDestroy(marker: TMarker): Observable<IMarkerDestroyEvent<TMap>>;

  // Event handling - All Events (for general event listening)
  abstract onMarkerEvents(marker: TMarker): Observable<IMarkerEventUnion<TMap>>;

  // Marker query and search
  abstract findMarkerById(id: string): TMarker | null;
  abstract getAllMarkers(): TMarker[];
  abstract getMarkersByAttribute(attribute: string, value: unknown): TMarker[];
}
